import { FormGroup } from '@angular/forms';
import {
    ApplyBookingResponseDto,
    PartyDetailDto,
} from '@opb-workspace/shared';
import { InstructivoEmbarqueService } from '../../../services/instructivo-embarque.service';
import {
    applyStep2PartySelection,
    Step2PartyUiHost,
} from './step2-party-actions.util';

function normPartyLabel(s: string | undefined | null): string {
    return (s ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, ' ');
}

/**
 * `CustomerId` del booking no siempre coincide con `tblParties.Id`; el JWT del portal
 * y/o el nombre del cliente permiten ubicar la fila en `GetShippers`.
 */
export function findOpbClientShipperInRows(
    rows: PartyDetailDto[],
    applied: ApplyBookingResponseDto,
    portalCustomerId: number | null,
    displayNameFallback?: string | null,
): PartyDetailDto | undefined {
    const idCandidates = new Set<number>();
    if (portalCustomerId != null && Number.isFinite(portalCustomerId)) {
        idCandidates.add(Math.trunc(portalCustomerId));
    }
    if (Number.isFinite(applied.customerId) && applied.customerId > 0) {
        idCandidates.add(Math.trunc(applied.customerId));
    }
    for (const id of idCandidates) {
        const byId = rows.find((r) => r.partyId === id);
        if (byId) {
            return byId;
        }
    }
    const rawLabel = (applied.customerName ?? displayNameFallback ?? '')
        .toString()
        .trim();
    const target = normPartyLabel(rawLabel);
    if (target.length) {
        const exact = rows.find((r) => {
            const labels = [
                normPartyLabel(r.shortName),
                normPartyLabel(r.name),
                normPartyLabel(r.fullName),
            ];
            return labels.some((l) => l.length > 0 && l === target);
        });
        if (exact) {
            return exact;
        }
    }
    return undefined;
}

/** Parte mínima mientras llega `GetShippers` o si no hay fila coincidente. */
export function syntheticCustomerShipperFromApply(
    applied: ApplyBookingResponseDto,
    portalCustomerId: number | null,
    displayNameFallback?: string | null,
): PartyDetailDto {
    let partyId = 0;
    if (Number.isFinite(applied.customerId) && applied.customerId > 0) {
        partyId = Math.trunc(applied.customerId);
    } else if (
        portalCustomerId != null &&
        Number.isFinite(portalCustomerId) &&
        portalCustomerId > 0
    ) {
        partyId = Math.trunc(portalCustomerId);
    }
    const label =
        (applied.customerName ?? '').trim() ||
        (displayNameFallback ?? '').trim() ||
        '';
    return {
        partyId,
        name: label,
        fullName: label,
        shortName: label,
        taxIdentificationNumber: applied.customerTaxIdentificationNumber,
        email: applied.customerContactEmail,
        contactName: applied.customerAttentionContactName,
        address: applied.customerAddress,
    };
}

/**
 * Tras aplicar reserva OPB: exportador por defecto = cliente del portal / booking.
 * 1) Selección síncrona inmediata (evita combo vacío hasta el HTTP).
 * 2) Enriquecimiento con fila real de `GetShippers` si existe.
 */
export function applyOpbDefaultClientExporterAfterBookingApplied(
    service: InstructivoEmbarqueService,
    form: FormGroup,
    host: Step2PartyUiHost,
    applied: ApplyBookingResponseDto,
    portalCustomerId: number | null,
    onComplete: () => void,
    /** Nombre cliente en listado OPB / portal si `applied.customerName` viene vacío. */
    displayNameFallback?: string | null,
): void {
    const syncParty = syntheticCustomerShipperFromApply(
        applied,
        portalCustomerId,
        displayNameFallback,
    );
    if (syncParty.partyId > 0) {
        applyStep2PartySelection(form, 'shipper', syncParty, host);
    } else if (syncParty.name) {
        form.patchValue({ shipperName: syncParty.name }, { emitEvent: false });
        host.shipperOptions = [syncParty];
    }

    service.searchParties('shipper', '').subscribe({
        next: (rows) => {
            const match = findOpbClientShipperInRows(
                rows,
                applied,
                portalCustomerId,
                displayNameFallback,
            );
            if (match) {
                applyStep2PartySelection(form, 'shipper', match, host);
            }
            onComplete();
        },
        error: () => onComplete(),
    });
}
